// src/components/commandSearch/commandRegistry.js
//
// Command Search Static Registry
// ==============================
//
// PURPOSE
// -------
// This file contains the static application commands used by the Command Search
// feature.
//
// IMPORTANT ARCHITECTURAL DECISION
// --------------------------------
// This registry is NOT owned by Navbar.
// Navbar should only render a button that opens Command Search.
//
// The command registry is intentionally separated because Command Search will
// grow into a cross-application feature:
// - Stage 1: static pages/actions from this registry.
// - Stage 2: backend-powered global search.
// - Stage 3: possibly richer command interpretation later.
//
// ROLE / PERMISSION MODEL
// -----------------------
// The optional `roles` field is a FRONTEND UX filter only.
//
// It prevents users from seeing obvious commands that are not relevant for
// their role, but it is NOT a security boundary.
//
// Real record-level and module-level security must always be enforced by the
// backend routes and by the future backend global search endpoint.
//
// If a command has no `roles` property or an empty roles array, it is visible
// to every authenticated user.
//
// NOTE ABOUT ROUTES
// -----------------
// Adjust only `path` values if your router uses different paths.
//
// I18N STRATEGY
// -------------
// The registry keeps only routing/action metadata plus English developer-readable
// display fallbacks. Visible UI strings and language-specific search aliases live
// in commandSearch.json.
//
// Keeping keywords in translation files is intentional:
// - commandRegistry.js remains language-neutral,
// - each locale can define its own practical synonyms,
// - future languages can be added without editing the route registry.
//
// Command Search localizes static commands before filtering, so translated
// command names, subtitles and search aliases can be searched by the user.
// Backend search results remain payload-owned and are not translated in this file.

function commandI18n(id, groupKey) {
  return {
    groupKey,
    titleKey: `commands.${id}.title`,
    subtitleKey: `commands.${id}.subtitle`,
    keywordsKey: `commands.${id}.keywords`,
  };
}

function translateValue(t, key, fallback) {
  if (!key) return fallback;
  return t(key, { defaultValue: fallback });
}

function translateKeywords(t, key) {
  if (!key) return [];

  const value = t(key, {
    returnObjects: true,
    defaultValue: [],
  });

  if (Array.isArray(value)) {
    return value
      .map((keyword) => String(keyword || "").trim())
      .filter(Boolean);
  }

  /*
    Defensive fallback for accidental JSON shape mistakes.

    Keywords are expected to be arrays in commandSearch.json. If someone later
    stores them as a comma-separated string, the search still remains usable
    instead of silently dropping all aliases.
  */
  if (typeof value === "string") {
    return value
      .split(",")
      .map((keyword) => keyword.trim())
      .filter(Boolean);
  }

  return [];
}

export function localizeCommandItems(items, t) {
  /*
    Localize display and search fields.

    Do not modify:
    - id/path/action fields: they are routing contracts,
    - roles: they are frontend UX filters.

    Keywords are deliberately injected from commandSearch.json at localization
    time. This keeps the static route registry free from language-specific search
    aliases while preserving the same Command Search behavior for users.
  */
  return (Array.isArray(items) ? items : []).map((item) => {
    const meta = item.i18n || {};

    return {
      ...item,
      group: translateValue(t, meta.groupKey, item.group),
      title: translateValue(t, meta.titleKey, item.title),
      subtitle: translateValue(t, meta.subtitleKey, item.subtitle),
      keywords: translateKeywords(t, meta.keywordsKey),
    };
  });
}


const MASTER_DATA_COMMANDS = [
  /*
    Master Data commands intentionally live in Command Search as explicit
    navigation targets instead of being inferred from Sidebar.

    The Sidebar defines visual navigation, while Command Search is a separate
    cross-application launcher with its own registry and localized aliases.
    Keeping these entries explicit prevents accidental coupling between menu
    rendering and search behavior, and makes it possible to add rich synonyms
    such as "company", "seller", "invoice footer" or Polish business terms.
  */
  {
    id: "master-data-company",
    type: "page",
    group: "Master Data",
    title: "Company Master Data",
    subtitle: "Maintain seller/company identity, addresses, bank accounts and print data",
    path: "/master-data/company",
    i18n: commandI18n("masterDataCompany", "groups.masterData"),
  },
  {
    id: "master-data-document-settings",
    type: "page",
    group: "Master Data",
    title: "Document Settings",
    subtitle: "Maintain invoice/document defaults used during posting and printing",
    path: "/master-data/document-settings",
    i18n: commandI18n("masterDataDocumentSettings", "groups.masterData"),
  },
  {
    id: "master-data-payment-methods",
    type: "page",
    group: "Master Data",
    title: "Payment Methods",
    subtitle: "Maintain payment methods used by sales documents and invoices",
    path: "/master-data/payment-methods",
    i18n: commandI18n("masterDataPaymentMethods", "groups.masterData"),
  },
  {
    id: "master-data-tax-settings",
    type: "page",
    group: "Master Data",
    title: "Tax Settings",
    subtitle: "Prepare tax/VAT settings for future sales document logic",
    path: "/master-data/tax-settings",
    i18n: commandI18n("masterDataTaxSettings", "groups.masterData"),
  },
  {
    id: "master-data-sales-number-series",
    type: "page",
    group: "Master Data",
    title: "Sales Number Series",
    subtitle: "Maintain numbering series for invoices, receipts and internal sales documents",
    path: "/master-data/sales-number-series",
    i18n: commandI18n("masterDataSalesNumberSeries", "groups.masterData"),
  },
  {
    id: "master-data-locations-branches",
    type: "page",
    group: "Master Data",
    title: "Locations / Branches",
    subtitle: "Prepare branch/location master data for future document and operational use",
    path: "/master-data/locations-branches",
    i18n: commandI18n("masterDataLocationsBranches", "groups.masterData"),
  },
  {
    id: "master-data-legal-texts",
    type: "page",
    group: "Master Data",
    title: "Legal Texts / Footer Texts",
    subtitle: "Maintain invoice footers, legal notes and printed document texts",
    path: "/master-data/legal-texts",
    i18n: commandI18n("masterDataLegalTexts", "groups.masterData"),
  },
];

const REPORT_COMMANDS = [
  {
    id: "reports-hub",
    type: "page",
    group: "Reports",
    title: "Reports",
    subtitle: "Open reports overview / reports hub",
    path: "/reports",
    i18n: commandI18n("reportsHub", "groups.reports"),
  },
  {
    id: "report-income",
    type: "report",
    group: "Reports",
    title: "Income Report",
    subtitle: "Revenue / income report",
    path: "/reports/income",
    roles: ["superuser"],
    i18n: commandI18n("incomeReport", "groups.reports"),
  },
  {
    id: "report-commission",
    type: "report",
    group: "Reports",
    title: "Commission Report",
    subtitle: "Employee commission report",
    path: "/reports/commission",
    i18n: commandI18n("commissionReport", "groups.reports"),
  },
  {
    id: "report-slot-performance",
    type: "report",
    group: "Reports",
    title: "Slot Occupancy Report",
    subtitle: "Analyze appointment slot usage",
    path: "/reports/slot-performance",
    i18n: commandI18n("slotOccupancyReport", "groups.reports"),
  },
  {
    id: "report-service-profitability",
    type: "report",
    group: "Reports",
    title: "Service Profitability Report",
    subtitle: "Analyze profitability by service",
    path: "/reports/service-profitability",
    i18n: commandI18n("serviceProfitabilityReport", "groups.reports"),
  },
  {
    id: "report-category-economics",
    type: "report",
    group: "Reports",
    title: "Category Economics Report",
    subtitle: "Analyze revenue and profitability by category",
    path: "/reports/category-economics",
    i18n: commandI18n("categoryEconomicsReport", "groups.reports"),
  },
  {
    id: "report-weekday-economics",
    type: "report",
    group: "Reports",
    title: "Weekday Economics Report",
    subtitle: "Analyze business performance by weekday",
    path: "/reports/weekday-economics",
    i18n: commandI18n("weekdayEconomicsReport", "groups.reports"),
  },
  {
    id: "report-idle-windows",
    type: "report",
    group: "Reports",
    title: "Idle Windows Report",
    subtitle: "Analyze gaps and unused working time",
    path: "/reports/idle-windows",
    i18n: commandI18n("idleWindowsReport", "groups.reports"),
  },
  {
    id: "report-daily-schedule-quality",
    type: "report",
    group: "Reports",
    title: "Daily Schedule Quality Report",
    subtitle: "Analyze quality of daily schedule structure",
    path: "/reports/daily-schedule-quality",
    i18n: commandI18n("dailyScheduleQualityReport", "groups.reports"),
  },
  {
    id: "report-no-show-loss",
    type: "report",
    group: "Reports",
    title: "No-Show Loss Report",
    subtitle: "Estimate lost revenue from not-performed visits",
    path: "/reports/no-show-loss",
    i18n: commandI18n("noShowLossReport", "groups.reports"),
  },
  {
    id: "report-client-retention-lite",
    type: "report",
    group: "Reports",
    title: "Client Retention Lite Report",
    subtitle: "Analyze client return behavior",
    path: "/reports/client-retention-lite",
    i18n: commandI18n("clientRetentionLiteReport", "groups.reports"),
  },
  {
    id: "report-premium-windows",
    type: "report",
    group: "Reports",
    title: "Premium Window Report",
    subtitle: "Analyze valuable time windows",
    path: "/reports/premium-windows",
    i18n: commandI18n("premiumWindowReport", "groups.reports"),
  },
  {
    id: "report-trend-seasonality",
    type: "report",
    group: "Reports",
    title: "Trend & Seasonality Report",
    subtitle: "Analyze demand trends and seasonality",
    path: "/reports/trend-seasonality",
    i18n: commandI18n("trendSeasonalityReport", "groups.reports"),
  },
];

export const COMMAND_ITEMS = [
  {
    id: "dashboard",
    type: "page",
    group: "Pages",
    title: "Dashboard",
    subtitle: "Go to dashboard / inbox overview",
    path: "/dashboard",
    i18n: commandI18n("dashboard", "groups.pages"),
  },
  ...MASTER_DATA_COMMANDS,

  {
    id: "upload-data",
    type: "page",
    group: "Pages",
    title: "Upload Data",
    subtitle: "Import / upload visit data",
    path: "/upload",
    i18n: commandI18n("uploadData", "groups.pages"),
  },
  {
    id: "visits-overview",
    type: "page",
    group: "Visits",
    title: "Visits Overview",
    subtitle: "Open visits table overview",
    path: "/visits/table",
    i18n: commandI18n("visitsOverview", "groups.visits"),
  },
  {
    id: "visits-calendar",
    type: "page",
    group: "Visits",
    title: "Visits Calendar",
    subtitle: "Open calendar / resource day view",
    path: "/visits/calendar",
    i18n: commandI18n("visitsCalendar", "groups.visits"),
  },
  {
    id: "vouchers-overview",
    type: "page",
    group: "Vouchers",
    title: "Voucher Overview",
    subtitle: "Browse and manage vouchers",
    path: "/vouchers/overview",
    i18n: commandI18n("voucherOverview", "groups.vouchers"),
  },
  {
    id: "voucher-new",
    type: "action",
    group: "Actions",
    title: "Create Voucher",
    subtitle: "Create a new voucher",
    path: "/vouchers/voucher",
    i18n: commandI18n("createVoucher", "groups.actions"),
  },
  {
    id: "products",
    type: "page",
    group: "Catalog",
    title: "Products",
    subtitle: "Browse product catalog",
    path: "/catalog/products",
    i18n: commandI18n("products", "groups.catalog"),
  },
  {
    id: "add-product",
    type: "action",
    group: "Actions",
    title: "Add product",
    subtitle: "Create a new product / SKU",
    path: "/catalog/products/new",
    i18n: commandI18n("addProduct", "groups.actions"),
  },
  {
    id: "services",
    type: "page",
    group: "Catalog",
    title: "Services",
    subtitle: "Browse service catalog",
    path: "/catalog/services",
    i18n: commandI18n("services", "groups.catalog"),
  },
  {
    id: "add-service",
    type: "action",
    group: "Actions",
    title: "Add service",
    subtitle: "Create a new service",
    path: "/catalog/services/new",
    i18n: commandI18n("addService", "groups.actions"),
  },
  {
    id: "service-categories",
    type: "page",
    group: "Catalog",
    title: "Service Categories",
    subtitle: "Maintain service main categories",
    path: "/catalog/service-categories",
    i18n: commandI18n("serviceCategories", "groups.catalog"),
  },
  {
    id: "service-sections",
    type: "page",
    group: "Catalog",
    title: "Service Sections",
    subtitle: "Maintain service sections",
    path: "/catalog/service-sections",
    i18n: commandI18n("serviceSections", "groups.catalog"),
  },
  {
    id: "product-categories",
    type: "page",
    group: "Catalog",
    title: "Product Categories",
    subtitle: "Maintain product categories",
    path: "/catalog/product-categories",
    i18n: commandI18n("productCategories", "groups.catalog"),
  },
  {
    id: "website-publication",
    type: "page",
    group: "Catalog",
    title: "Website Publication",
    subtitle: "Prepare website publication payload",
    path: "/catalog/publication",
    i18n: commandI18n("websitePublication", "groups.catalog"),
  },
  // Sales document commands. Sales is intentionally separate from Stock: posting
  // a product line calls stockMovementService and creates a SALE movement, but
  // the commercial document itself belongs to the Sales module.
  {
    id: "sales-documents",
    type: "page",
    group: "Sales",
    title: "Sales Documents",
    subtitle: "Browse reception sales, drafts, invoices and receipts",
    path: "/sales",
    i18n: commandI18n("salesDocuments", "groups.sales"),
  },
  {
    id: "sales-new",
    type: "action",
    group: "Sales",
    title: "New Sales Document",
    subtitle: "Register a new product/service sale",
    path: "/sales/new",
    i18n: commandI18n("newSalesDocument", "groups.sales"),
  },

  // Web Shop orders are separated from reception Sales because their records are
  // created by the public checkout/payment flow. The overview is read-only for
  // all internal users; maintenance actions are guarded by backend roles.
  {
    id: "web-shop-orders",
    type: "page",
    group: "Web Shop",
    title: "Web Shop Orders",
    subtitle: "Browse online shop orders, payment status and voucher email state",
    path: "/web-shop/orders",
    i18n: commandI18n("webShopOrders", "groups.webShop"),
  },

  // Stock foundation route. The page focuses on inventory quantities/value and
  // movement audit; normal product sales should be posted from Sales Documents.
  {
    id: "stock-overview",
    type: "page",
    group: "Stock",
    title: "Stock Overview",
    subtitle: "Browse inventory quantities, value and movements",
    path: "/stock",
    i18n: commandI18n("stockOverview", "groups.stock"),
  },
  /*
    Stock master-data commands.

    These pages are visible in the Stock sidebar branch, but Command Search does
    not infer commands from Sidebar. They must be registered here explicitly so
    the navbar search can navigate to stock locations and units of measure.

    Both routes are currently protected for superuser in App.js. The frontend
    roles filter mirrors that route contract for UX only; backend route guards
    remain the real security boundary.
  */
  {
    id: "stock-locations",
    type: "page",
    group: "Stock",
    title: "Stock Locations",
    subtitle: "Maintain warehouses, reception shelves and salon stock locations",
    path: "/stock/locations",
    roles: ["superuser"],
    i18n: commandI18n("stockLocations", "groups.stock"),
  },
  {
    id: "units-of-measure",
    type: "page",
    group: "Stock",
    title: "Units of Measure",
    subtitle: "Maintain units used by products, stock and service consumption",
    path: "/stock/units-of-measure",
    roles: ["superuser"],
    i18n: commandI18n("unitsOfMeasure", "groups.stock"),
  },
  {
    id: "unit-conversions",
    type: "page",
    group: "Stock",
    title: "Unit Conversions",
    subtitle: "Maintain physical conversion factors used by package-aware stock receipts",
    path: "/stock/unit-conversions",
    roles: ["superuser"],
    i18n: commandI18n("unitConversions", "groups.stock"),
  },
  {
    id: "material-exceptions",
    type: "page",
    group: "Stock",
    title: "Material Exceptions",
    subtitle: "Review service materials that could not be consumed automatically",
    path: "/stock/material-exceptions",
    i18n: commandI18n("materialExceptions", "groups.stock"),
  },
  {
    id: "sanitary-products",
    type: "page",
    group: "Sanitary documentation",
    title: "Sanitary products",
    subtitle: "Maintain sanitary profiles for catalog products",
    path: "/sanitary/products",
    i18n: commandI18n("sanitaryProducts", "groups.sanitary"),
  },
  {
    id: "sanitary-rules",
    type: "page",
    group: "Sanitary documentation",
    title: "Sanitary consumption rules",
    subtitle: "Maintain versioned disinfection and hygiene consumption rules",
    path: "/sanitary/rules",
    i18n: commandI18n("sanitaryRules", "groups.sanitary"),
  },
  {
    id: "sanitary-daily-settlement",
    type: "page",
    group: "Sanitary documentation",
    title: "Daily hygiene settlement",
    subtitle: "Calculate normative disinfection and hygiene consumption for a day or period",
    path: "/sanitary/daily-settlement",
    i18n: commandI18n("sanitaryDailySettlement", "groups.sanitary"),
  },
  {
    id: "sanitary-events",
    title: "Sanitary hygiene events",
    subtitle: "Review confirmations, corrections, rejections and stock postings",
    path: "/sanitary/events",
    keywords: ["sanitary", "events", "hygiene", "confirmations", "stock"],
    i18n: commandI18n("sanitaryEvents", "groups.sanitary"),
  },
  {
    id: "sanitary-solutions",
    type: "page",
    group: "Sanitary documentation",
    title: "Working solutions",
    subtitle: "Register preparation of diluted working solutions from concentrates",
    path: "/sanitary/solutions",
    i18n: commandI18n("sanitarySolutions", "groups.sanitary"),
  },
  {
    id: "sanitary-work-patterns",
    type: "page",
    group: "Sanitary documentation",
    title: "Sanitary work patterns",
    subtitle: "Configure facility operating hours used by daily sanitary rules",
    path: "/sanitary/work-patterns",
    i18n: commandI18n("sanitaryWorkPatterns", "groups.sanitary"),
  },
  {
    id: "sanitary-reports",
    type: "page",
    group: "Sanitary documentation",
    title: "Hygiene reports",
    subtitle: "Generate hygiene and disinfection operational reports for a period",
    path: "/sanitary/reports",
    keywords: ["sanitary", "hygiene", "report", "pdf", "excel", "disinfection"],
    i18n: commandI18n("sanitaryReports", "groups.sanitary"),
  },
  {
    id: "sanitary-daily-closing",
    type: "page",
    group: "Sanitary documentation",
    title: "Automated sanitary daily closing",
    subtitle: "Configure and run D+N automated sanitary calculation and stock posting",
    path: "/sanitary/daily-closing",
    roles: ["superuser"],
    keywords: ["sanitary", "daily closing", "automation", "scheduler", "D+2", "stock posting"],
    i18n: commandI18n("sanitaryDailyClosing", "groups.sanitary"),
  },
  {
    id: "administration",
    type: "page",
    group: "Pages",
    title: "Administration",
    subtitle: "Open administration area",
    path: "/admin-panel",
    roles: ["superuser"],
    i18n: commandI18n("administration", "groups.pages"),
  },

  ...REPORT_COMMANDS,
];


